/**
 * Recently deleted installers — 30-day recovery bin.
 *
 * Lists every soft-deleted installer inside the window with a "Restore"
 * button and a countdown pill. Once an installer passes 30 days the
 * backend `startup.py` purge job hard-deletes them and they fall off this
 * list.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, RotateCcw, Trash2, Clock, RefreshCw, Users } from 'lucide-react';

import ProPortalHeader from '@/components/ProPortalHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  return { 'Content-Type': 'application/json' };
}

export default function DeletedInstallersPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/staff/deleted-installers`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setItems(await r.json());
    } catch (e) {
      toast.error('Could not load bin', { description: String(e).slice(0, 200) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const restore = async (u) => {
    if (busyId) return;
    setBusyId(u.user_id);
    try {
      const r = await fetch(`${API_URL}/api/staff/users/${u.user_id}/restore`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      toast.success(`Restored ${u.name}`);
      setItems((list) => list.filter((x) => x.user_id !== u.user_id));
    } catch (e) {
      toast.error(String(e.message || e).slice(0, 200));
    } finally {
      setBusyId(null);
    }
  };

  const sorted = useMemo(
    () => [...items].sort((a, b) => (a.days_remaining || 0) - (b.days_remaining || 0)),
    [items],
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="deleted-installers-page">
      <ProPortalHeader activeSection="staff" />
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/staff/installers')} className="p-2 hover:bg-gray-200 rounded-lg" data-testid="deleted-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Trash2 className="w-5 h-5 text-red-600" />
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Recently Deleted</h1>
              <span className="text-xs font-semibold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5" data-testid="deleted-count">
                {items.length}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              Installers you've deleted can be restored within 30 days. After that they're purged from the system.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="deleted-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading…</div>
        ) : sorted.length === 0 ? (
          <div className="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center" data-testid="deleted-empty">
            <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">The bin is empty — nothing to restore.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {sorted.map((u) => {
              const daysLeft = u.days_remaining ?? 0;
              const urgent = daysLeft <= 3;
              return (
                <Card
                  key={u.user_id}
                  className={`hover:shadow-md transition ${urgent ? 'border-red-300' : 'border-gray-200'}`}
                  data-testid={`deleted-row-${u.user_id}`}
                >
                  <CardContent className="p-3 flex items-center gap-3 flex-wrap">
                    <div className="w-10 h-10 rounded-full bg-red-50 border border-red-100 flex items-center justify-center shrink-0">
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 truncate">{u.name}</div>
                      <div className="text-xs text-gray-500 truncate">
                        {u.company_name ? `${u.company_name} · ` : ''}{u.email}
                      </div>
                      {u.deleted_by_name && (
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          Deleted by {u.deleted_by_name} on {new Date(u.deleted_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border shrink-0 ${
                        urgent
                          ? 'bg-red-100 text-red-800 border-red-200 animate-pulse'
                          : 'bg-amber-50 text-amber-800 border-amber-200'
                      }`}
                      data-testid={`deleted-days-${u.user_id}`}
                    >
                      <Clock className="w-3 h-3" />
                      {daysLeft} day{daysLeft === 1 ? '' : 's'} left
                    </span>
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-500 shrink-0"
                      disabled={busyId === u.user_id}
                      onClick={() => restore(u)}
                      data-testid={`deleted-restore-${u.user_id}`}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      {busyId === u.user_id ? 'Restoring…' : 'Restore'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
